import React, { useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import htmlDocx from 'html-docx-js/dist/html-docx';
import FileSaver from 'file-saver';
import { useTranslation, Trans } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { updateDoc, removeDoc } from 'actions/docs';
import { showModal } from 'actions/modal';

import { makeGetDoc } from 'selectors/docs';

import { generatePDF } from 'api/docs-api';

import { useCurrentUserId } from 'shared-hooks/user';

import { getDocPermission } from 'utils/doc';
import { download } from 'utils/helpers';
import { trackEvent } from 'services/events-tracker-service';
import { createAlert } from 'services/alert-service';

import DropdownBtn from 'components/dropdown-btn';

import IconMoreHoriz from 'icons/icon-more-horiz';

const EMPTY_ARRAY = [];

const DocActions = ({ id, members, archived, name, project, googleType, externalId, accessType, inModal, inDoc, multipage, btnClassName = 'btn btn-xxsmall btn-icon', getDocPrintHtml, onEditName, onArchive, onShow, onHide }) => {
    const { t } = useTranslation(['doc', 'common']);
    const history = useHistory();
    const currentUserId = useCurrentUserId();

    const exportDOCX = useCallback(async () => {
        if (googleType) {
            download(`https://docs.google.com/${googleType}/d/${externalId}/export?format=docx`);
            return;
        }

        const alert = createAlert('success', t('export.progress'));

        try {
            const { html, name }  = await getDocPrintHtml(false, false)

            const fileBlob = htmlDocx.asBlob(html);

            const save = () => {
                FileSaver.saveAs(fileBlob,`${name}.docx`);
            }
            save();

            alert.setType('success').setContent(
                <div>
                    <Trans
                        i18nKey="doc:export.save"
                        components={{
                            Save: <u onClick={save} style={{cursor: 'pointer'}} />
                        }}
                    />
                </div>
            );
        } catch (e) {
            alert.setType('error').setContent(t('export.error'));
        }
    }, [googleType, externalId, name, getDocPrintHtml]);

    const exportPDF = useCallback(async singlePage => {
        if (googleType) {
            download(`https://docs.google.com/${googleType}/d/${externalId}/export?format=pdf`);
            return;
        }

        const alert = createAlert('success', t('export.progress'), {
            duration: 0
        });

        try {
            const { html, name } = await getDocPrintHtml(singlePage);

            const result = await generatePDF({ html, filename: name + '.pdf' });

            alert.setType('success').setContent(
                <div>
                    <Trans
                        i18nKey="doc:export.save"
                        components={{
                            Save: <a id="doc-download" download={name + '.pdf'} href={result.data.pdf} />
                        }}
                    />
                </div>
            ).hide(3000);

            setTimeout(() => {
                document.getElementById('doc-download') && document.getElementById('doc-download').click();
            }, 100);
        } catch (e) {
            alert.setType('error').setContent(t('export.error')).hide(3000);
        }
    }, [googleType, externalId, name, getDocPrintHtml]);

    const handleActionClick = useCallback(({ payload }) => {
        switch (payload.action) {
            case 'archive':
                trackEvent('DOC.ARCHIVE', {
                    pageName: 'Docs',
                    docName: name
                });

                if (inModal) {
                    onArchive && onArchive();
                } else if (inDoc) {
                    history.push(`/${project}/documents`);
                }

                updateDoc(id, {
                    archived: true
                });
                break;

            case 'unarchive':
                trackEvent('DOC.UNARCHIVE', {
                    via: 'Docs'
                });

                updateDoc(id, {
                    archived: false
                });
                break;

            case 'editName':
                onEditName();
                break;

            case 'remove':
                trackEvent('DOC.REMOVE', {
                    via: 'Docs'
                });

                removeDoc(id);
                break;

            case 'share':
                trackEvent('DOC.URL_COPY', {
                    via: 'Docs'
                });

                showModal('SHARE_LINKS', {
                    type: 'document',
                    docId: id,
                    projectId: project
                })
                break;

            case 'exportPDF':
                exportPDF();
                break;

            case 'exportPagePDF':
                exportPDF(true);
                break;

            case 'exportDOCX':
                exportDOCX();
                break;

            case 'makePublic':
                updateDoc(id, {
                    access_type: 'public'
                });

            case 'makeLimited':
                updateDoc(id, {
                    access_type: 'limited'
                });
                break;

            default:
                // Es lint
        }
    }, [id, project, name, exportPDF, exportDOCX]);

    const permission = useMemo(() => {
        return getDocPermission(members, currentUserId);
    }, [members, currentUserId])

    const actions = useMemo(() => {
        const items = [];

        items.push({
            text: t('common:Share'),
            payload: {
                action: 'share'
            }
        });

        if (permission === 'edit') {
            if (onEditName) {
                items.push({
                    text: t('common:Edit name'),
                    payload: {
                        action: 'editName'
                    }
                });
            }

            if (archived) {
                items.push({
                    text: t('common:Unarchive'),
                    payload: {
                        action: 'unarchive'
                    }
                });
            } else {
                items.push({
                    text: t('actions.archive.title'),
                    payload: {
                        action: 'archive'
                    },
                    prompt: {
                        title: t('actions.archive.title'),
                        text: t('actions.archive.description'),
                        confirmText: t('common:Archive'),
                        confirmClassName: 'btn-caution'
                    }
                });
            }

            if (accessType === 'limited') {
                items.push({
                    text: t('actions.accessType.public'),
                    payload: {
                        action: 'makePublic'
                    }
                });
            } else {
                items.push({
                    text: t('actions.accessType.limited'),
                    payload: {
                        action: 'makeLimited'
                    }
                });
            }
        }

        if (getDocPrintHtml) {
            items.push({
                text: t('actions.exportToPDF'),
                payload: {
                    action: 'exportPDF'
                }
            });

            if (multipage) {
                items.push({
                    text: t('actions.exportSinglePageToPDF'),
                    payload: {
                        action: 'exportPagePDF'
                    }
                });
            }

            // items.push({
            //     text: 'Export to .docx',
            //     payload: {
            //         action: 'exportDOCX'
            //     }
            // });
        }

        if (permission === 'edit') {
            items.push({
                text: t('actions.delete.title'),
                payload: {
                    action: 'remove'
                },
                prompt: {
                    title: t('actions.delete.title'),
                    text: t('actions.delete.description'),
                    confirmText: t('common:Delete'),
                    confirmClassName: 'btn-caution'
                }
            });
        }

        return items;
    }, [archived, permission, multipage, accessType, onEditName, getDocPrintHtml, t]);

    return (
        <DropdownBtn
            btnClassName={btnClassName}
            items={actions}
            onDropdownShow={onShow}
            onDropdownHide={onHide}
            onItemClick={handleActionClick}
        >
            <IconMoreHoriz />
        </DropdownBtn>
    )
}

const makeMapStateToProps = () => {;
    const getDoc = makeGetDoc();

    return ({ docs }, ownProps) => {
        const doc = getDoc(docs.items, ownProps.id);

        return {
            name: doc?.name,
            archived: doc?.archived,
            project: doc?.project,
            members: doc?.members || EMPTY_ARRAY,
            googleType: doc?.subtype,
            externalId: doc?.external_id,
            accessType: doc?.access_type
        }
    };    
}

export default connect(makeMapStateToProps)(DocActions);
